import React, { useCallback } from 'react';
import { Moment } from 'moment';
import { isEqual } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

// MUI components
import {
  Grid,
  Card,
  CardContent,
  WithStyles,
  withStyles,
} from '@material-ui/core';

// components
import Table from 'components/Table';
import AnalyticsActionBar from 'components/AnalyticsActionBar';
import TagsAnalyticCell from 'routes/NewMemberAnalytics/components/TagsAnalyticCell';

// actions
import { createTaskNewMembersListReport } from 'store/actions/apiTaskActions';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getForm from 'store/selectors/getForm';
import getSliceEntityById from 'store/selectors/getSliceEntityById';
import getIsCouncilAdmin from 'store/selectors/getIsCouncilAdmin';
import getIsHqAdmin from 'store/selectors/getIsHqAdmin';

// helpers
import { arrayToStringWithoutEmpty } from 'helpers/transform';

// icons
import BookIcon from '@material-ui/icons/Book';

// styles
import newMemberListStyles from 'routes/NewMemberAnalytics/NewMemberList/newMemberList.style';

const REPORT_FORM = 'chapterNewMemberListReportForm';
const ROWS_PER_PAGE = 10;

export interface ContentType {
  emailAddress: string | null;
  group: string | null;
  lead: string | null;
  leadId: number | null;
  phoneNumber: string | null;
  referralSource: string[] | null;
  status: string | null;
  tags: (string | number)[][] | null;
}

export interface ColumnType {
  key: string;
  label: string;
}

export interface NewMemberListProps
  extends WithStyles<typeof newMemberListStyles> {
  isOrganizationMemberAnalytics?: boolean;
}

const DISABLE_SORT = ['tags'];

const NewMemberList = ({
  classes,
  isOrganizationMemberAnalytics,
}: NewMemberListProps) => {
  const dispatch = useDispatch();

  const {
    currentGroupId,
    isCouncilAdmin,
    isHqAdmin,
    report: {
      data: { content = [], meta: { columns = [] } = {} } = {},
      id: reportId,
    },
    form: { isSubmitting },
  } = useSelector(
    state => ({
      currentGroupId: getCurrentGroupId(state),
      isCouncilAdmin: getIsCouncilAdmin(state),
      isHqAdmin: getIsHqAdmin(state),
      form: getForm(REPORT_FORM)(state),
      report: getSliceEntityById(
        'report',
        'ReportTypes.NEW_MEMBERS_LIST',
        {}
      )(state),
    }),
    isEqual
  );

  const newContent = content.map((item: ContentType) => {
    const { tags, referralSource } = item || {};
    return {
      ...(item || {}),
      tags: tags ? <TagsAnalyticCell currentTags={tags} /> : '',
      referralSource: referralSource
        ? arrayToStringWithoutEmpty(referralSource)
        : '',
    };
  });

  const newColumns = columns.map(({ key, label }: ColumnType) => ({
    key,
    label,
    disableSort: DISABLE_SORT.indexOf(key) !== -1,
  }));

  const dispatchCreateReport = useCallback(
    ({
      startDate,
      endDate,
      forceRefresh,
      groupId = currentGroupId,
    }: {
      startDate: Moment;
      endDate: Moment;
      forceRefresh: boolean;
      groupId?: number;
    }) => {
      dispatch(
        createTaskNewMembersListReport({
          groupId,
          formName: REPORT_FORM,
          startDate,
          endDate,
          forceRefresh,
        })
      );
    },
    [dispatch, currentGroupId]
  );

  return (
    <Card>
      <CardContent className={classes.cardContent}>
        <Grid container spacing={2}>
          <AnalyticsActionBar
            onCreateReport={dispatchCreateReport}
            title={
              isOrganizationMemberAnalytics
                ? 'New Member Report'
                : 'Chapter New Member List'
            }
            subtitle={
              isOrganizationMemberAnalytics
                ? 'Pull a list of all new members or a select group'
                : 'Pull a list of all new members for a select group. This comes from the Chapter’s account.'
            }
            icon={BookIcon}
            isHqAdmin={isHqAdmin}
            currentGroupId={currentGroupId}
            newMembersReport
            isCouncilAdmin={isCouncilAdmin}
            loading={isSubmitting}
            reportId={reportId}
            isHqReport
          />

          <Grid item xs={12} className={classes.tableGrid}>
            <Table
              columns={newColumns}
              data={newContent}
              variant='outlined'
              numberOfSkeletonRows={isSubmitting ? 8 : 0}
              initialOrder='asc'
              initialOrderBy='lead'
              TablePaginationProps={{
                count: newContent.length,
                rowsPerPage: ROWS_PER_PAGE,
                rowsPerPageOptions: [ROWS_PER_PAGE],
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default withStyles(newMemberListStyles, { withTheme: true })(
  NewMemberList
);
